/**
 * ตรรกะล้วนของหน้าจอนำเข้า Forecast (P2-5a, D-093) แยกออกจาก JSX เพื่อให้ทดสอบกฎ
 * ได้ตรง ๆ
 *
 * แนวเดียวกับ `lib/sales/salesView.ts` และ `lib/procurement/procurementView.ts`:
 * สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดยไม่ต้องเรนเดอร์อะไรเลย — สี่ข้อที่ผิดแล้วแพง
 * ที่สุดของกลุ่มนี้
 *
 * 1. **token ของแถวที่ผิด แปลเป็นภาษาไทยว่าอะไร** (`forecastLineErrorLabel`) ซึ่งเป็น
 *    เหตุผลที่หน้าจอนี้มีอยู่: ผู้วางแผนต้องอ่านออกว่า **แถวไหน** ผิดและ **เพราะอะไร**
 *    ไม่ใช่เห็นแค่ป้าย "นำเข้าไม่สำเร็จ" แล้วต้องไปเดาเอาเองจากไฟล์ทั้งไฟล์
 * 2. **ใบนี้กด Publish ได้หรือยัง** (`canPublishForecastBatch`) ซึ่งเป็นการอ่าน
 *    state machine ของ contract ออกมาเป็นคำตอบเดียว ไม่ใช่เงื่อนไข `if` ที่กระจาย
 *    อยู่ใน JSX — และเป็นที่เดียวที่บอกว่า **`INVALID` ไม่มีปุ่ม Publish เลย**
 * 3. **การประกอบ body** (`toCreateForecastImportBody`) ซึ่งเป็นด่านเดียวที่ทางเขียน
 *    ของหน้าจอนี้เดินผ่าน
 * 4. **จำนวนกับวันที่ที่กรอกใช้ได้หรือยัง** — ตรวจเป็นสตริงล้วน ไม่แปลงเป็น number
 *    และ **ไม่แปลงวันที่ผ่าน `Date` แม้แต่ครั้งเดียว**
 *
 * ⚠️ ไฟล์นี้ **ไม่มีฟังก์ชันที่ประกอบ `status`, `batch_id`, `validation_error` หรือ
 * ฟิลด์ผู้กระทำเลย** และนั่นเป็นข้อจำกัดโดยเจตนา: ทั้งหมดเป็นของเซิร์ฟเวอร์ (D-093)
 * โดยเฉพาะ `validation_error` — คำตัดสินว่าแถวไหนผิดต้องมาจากกฎของระบบเสมอ หน้าจอ
 * ที่ตัดสินเองจะบอกผู้ใช้ว่าแถวไหนผิดด้วยกติกาชุดที่สองที่ไม่มีใครดูแล
 */

import {
  CANCELLED_BATCH_STATUS,
  DUPLICATE_FORECAST_LINE,
  INVALID_BATCH_STATUS,
  PRODUCT_NOT_ACTIVE,
  PRODUCT_NOT_FOUND,
  PUBLISHED_BATCH_STATUS,
  QUANTITY_MUST_NOT_BE_NEGATIVE,
  SUPERSEDED_BATCH_STATUS,
  UOM_NOT_FOUND,
  VALIDATED_BATCH_STATUS,
  type CreateForecastImportBody,
  type CreateForecastImportLine,
  type ForecastImportBatchSummary,
  type ForecastLine,
} from './types';

/* ------------------------------------------------------------- สถานะ ----- */

export interface ForecastStatusDescriptor {
  readonly code: string;
  readonly label: string;
  readonly summary: string;
}

/**
 * ห้าสถานะของ lifecycle ตามลำดับที่ contract เขียนไว้
 *
 * ข้อความของแต่ละสถานะต้องบอกว่ามัน **ต่างกันอย่างไร** ไม่ใช่แค่แปลชื่อ — และสองตัว
 * ท้ายต้องบอกด้วยว่า **ใบนั้นยังอยู่**: `SUPERSEDED` ไม่ได้ถูกลบ มันถูกแทนที่ และ
 * ผู้ใช้ที่เปิดมันขึ้นมาต้องอ่านออกว่านั่นคือสิ่งที่เกิดขึ้น ไม่ใช่ความผิดพลาด
 */
export const FORECAST_STATUS_DESCRIPTORS: readonly ForecastStatusDescriptor[] = [
  {
    code: VALIDATED_BATCH_STATUS,
    label: 'ตรวจผ่านแล้ว (VALIDATED)',
    summary:
      'ทุกบรรทัดในไฟล์ผ่านการตรวจ — **ยังไม่เป็น demand** จนกว่าผู้รับผิดชอบจะกด Publish (flow ข้อ 1.4)',
  },
  {
    code: INVALID_BATCH_STATUS,
    label: 'มีบรรทัดที่ผิด (INVALID)',
    summary:
      'มีอย่างน้อยหนึ่งบรรทัดที่ไม่ผ่านกฎ — ทุกบรรทัดถูกเก็บไว้ครบให้ดูว่าแถวไหนผิด แต่ใบนี้ Publish ไม่ได้',
  },
  {
    code: PUBLISHED_BATCH_STATUS,
    label: 'ประกาศใช้แล้ว (PUBLISHED)',
    summary:
      'ผ่านผู้รับผิดชอบแล้ว — เป็น forecast ฉบับที่ระบบใช้คำนวณ demand ของคู่โรงงานและลูกค้านี้',
  },
  {
    code: SUPERSEDED_BATCH_STATUS,
    label: 'ถูกแทนด้วยฉบับใหม่ (SUPERSEDED)',
    summary:
      'มี revision ใหม่ของคู่โรงงานและลูกค้าเดียวกันถูกประกาศใช้แทน — **ใบนี้ยังอยู่และยังเปิดอ่านได้** เพราะประวัติคือสิ่งที่ตอบว่าทำไมแผนเดือนก่อนถึงเป็นอย่างนั้น',
  },
  {
    code: CANCELLED_BATCH_STATUS,
    label: 'ยกเลิกแล้ว (CANCELLED)',
    summary:
      'ถูกยกเลิกไปแล้ว — ยังอ่านได้ตลอดไป ไม่ใช่การลบแถวทิ้ง (ยังไม่มีเส้นทางใดใน P2-5a ที่เขียนสถานะนี้)',
  },
];

/** สีของป้ายสถานะ — บอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าดีหรือแย่ */
export type ForecastStatusTone = 'neutral' | 'warning' | 'success' | 'info' | 'danger';

export function forecastBatchStatusTone(status: string): ForecastStatusTone {
  switch (status) {
    case VALIDATED_BATCH_STATUS:
      // สีเตือนเพราะมันคือใบที่ **รอคนตรวจแล้วกด** ไม่ใช่เพราะมีอะไรผิด
      return 'warning';
    case INVALID_BATCH_STATUS:
      // ตัวเดียวในชุดนี้ที่มีอะไรผิดจริง ๆ และต้องมีคนไปแก้ไฟล์
      return 'danger';
    case PUBLISHED_BATCH_STATUS:
      return 'success';
    case SUPERSEDED_BATCH_STATUS:
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * ชื่อที่อ่านออกของสถานะ — **คืนค่าดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนแถวนั้นหรือคืนช่องว่าง**
 *
 * ช่องสถานะที่ว่างเปล่าอ่านเหมือนใบที่ไม่มีสถานะ ซึ่งเป็นไปไม่ได้ตาม contract
 * (คอลัมน์นี้ `NOT NULL` และมี CHECK) รหัสดิบบอกผู้ใช้ได้อย่างน้อยว่าใบอยู่ที่ไหน
 */
export function forecastBatchStatusLabel(status: string): string {
  return (
    FORECAST_STATUS_DESCRIPTORS.find((entry) => entry.code === status)?.label ?? status
  );
}

/* --------------------------------------------- กฎราย line เป็นภาษาที่คนอ่าน -- */

export interface ForecastLineErrorDescriptor {
  readonly token: string;
  readonly label: string;
  readonly hint: string;
}

/**
 * ห้า token ที่ลงมาอยู่ใน `validation_error` แปลเป็นภาษาไทย — **หัวใจของหน้าจอนี้**
 *
 * แต่ละตัวต้องตอบสองอย่าง: **แถวนี้ผิดอะไร** (`label` ที่อยู่ในตารางข้างเลขบรรทัด)
 * และ **ต้องไปแก้ตรงไหน** (`hint`) — token ดิบ ๆ อย่าง `QUANTITY_MUST_NOT_BE_NEGATIVE`
 * บอกโปรแกรมเมอร์ได้ครบ แต่บอกคนที่ถือไฟล์ Excel ของลูกค้าอยู่ในมือไม่ได้เลย
 *
 * `hint` ของ `QUANTITY_MUST_NOT_BE_NEGATIVE` ต้องพูดถึง **ศูนย์** โดยเฉพาะ: กฎคือ
 * `>= 0` ไม่ใช่ `> 0` (D-093) ผู้ใช้ที่เห็นแค่ "จำนวนไม่ถูกต้อง" จะไปไล่ลบแถวที่เป็น
 * ศูนย์ออกจากไฟล์ ทั้งที่ forecast ของวันที่ไม่ต้องใช้อะไรเลยเป็นข้อมูลที่ถูกต้อง
 * และมีความหมาย
 */
export const FORECAST_LINE_ERROR_DESCRIPTORS: readonly ForecastLineErrorDescriptor[] = [
  {
    token: PRODUCT_NOT_FOUND,
    label: 'ไม่พบรหัสสินค้านี้ในระบบ',
    hint: 'รหัสในไฟล์ไม่ตรงกับสินค้าตัวใดในทะเบียน — ตรวจรหัสอีกครั้งจากหน้า "ข้อมูลสินค้า (Product Master)" แถวนี้จึงไม่มีรหัสสินค้าให้แสดง',
  },
  {
    token: DUPLICATE_FORECAST_LINE,
    label: 'ซ้ำกับบรรทัดก่อนหน้า (สินค้าเดียวกัน วันเดียวกัน)',
    hint: 'ไฟล์นี้ระบุสินค้าตัวเดียวกันของวันเดียวกันไว้มากกว่าหนึ่งแถว ซึ่งทำให้ตอบไม่ได้ว่าวันนั้นต้องใช้เท่าไร — รวมสองแถวเป็นแถวเดียวแล้วนำเข้าใหม่',
  },
  {
    token: PRODUCT_NOT_ACTIVE,
    label: 'สินค้ายังไม่พร้อมใช้งาน (ไม่ ACTIVE)',
    hint: 'มีสินค้ารหัสนี้อยู่จริง แต่ยังไม่ผ่านอนุมัติหรือถูกเลิกใช้ไปแล้ว — วางแผนกับสินค้าที่ยังไม่ ACTIVE ไม่ได้',
  },
  {
    token: QUANTITY_MUST_NOT_BE_NEGATIVE,
    label: 'จำนวนติดลบ',
    hint: 'จำนวนต้องไม่น้อยกว่าศูนย์ — **ศูนย์ใช้ได้** และหมายถึง "วันนั้นคาดว่าไม่ต้องใช้เลย" ที่ผิดคือค่าที่ติดลบเท่านั้น',
  },
  {
    token: UOM_NOT_FOUND,
    label: 'ไม่รู้จักหน่วยนับนี้',
    hint: 'หน่วยนับในไฟล์ไม่ใช่หน่วยที่ระบบนี้ขึ้นทะเบียนไว้ — ตรวจตัวสะกดของหน่วย (เช่น PC, EA, KG)',
  },
];

/**
 * แปล token ของบรรทัดเป็นภาษาไทย — **คืน token ดิบเมื่อไม่รู้จัก ไม่ใช่ซ่อนมัน**
 *
 * `null` เข้า = `null` ออก (บรรทัดนั้นผ่าน) แต่ token ที่หน้าจอยังไม่มีคำแปลให้
 * **ต้องแสดงออกมาดิบ ๆ** ไม่ใช่กลายเป็นช่องว่าง: ช่องว่างอ่านเหมือน "แถวนี้ไม่มี
 * ปัญหา" ซึ่งเป็นคำตอบที่ตรงข้ามกับความจริง และเป็นวิธีที่แถวผิดจะกลายเป็น demand
 * เงียบ ๆ ในความเข้าใจของผู้ใช้ ทั้งที่กฎ flow ข้อ 1.3 เขียนขึ้นมาเพื่อกันสิ่งนั้น
 */
export function forecastLineErrorLabel(token: string | null): string | null {
  if (token === null || token.trim() === '') {
    return null;
  }
  return (
    FORECAST_LINE_ERROR_DESCRIPTORS.find((entry) => entry.token === token)?.label ?? token
  );
}

/** คำแนะนำว่าต้องไปแก้อะไร — `null` เมื่อเป็น token ที่หน้าจอยังไม่รู้จัก */
export function forecastLineErrorHint(token: string | null): string | null {
  if (token === null || token.trim() === '') {
    return null;
  }
  return FORECAST_LINE_ERROR_DESCRIPTORS.find((entry) => entry.token === token)?.hint ?? null;
}

/** บรรทัดนี้เป็นบรรทัดที่ไม่ผ่านหรือไม่ — คำถามเดียวที่ตารางรายบรรทัดถาม */
export function isFailedForecastLine(line: ForecastLine): boolean {
  return line.validation_error !== null && line.validation_error.trim() !== '';
}

/** นับแถวที่ไม่ผ่านในใบหนึ่ง — ตัวเลขที่พาดหัวตารางบรรทัดต้องบอกก่อนใครอ่านตาราง */
export function countFailedForecastLines(lines: readonly ForecastLine[]): number {
  return lines.filter(isFailedForecastLine).length;
}

/* ------------------------------------------------------------ lifecycle -- */

/**
 * ใบนี้กด Publish ได้หรือไม่ — **`VALIDATED` เท่านั้น ที่เหลือไม่มีเลย**
 *
 * `INVALID` ไม่มีปุ่มเพราะเซิร์ฟเวอร์จะตอบ `422 FORECAST_BATCH_IS_INVALID` แน่นอน
 * (flow ข้อ 1.3 — แถวที่ผิดห้ามกลายเป็น demand) ส่วน `PUBLISHED`/`SUPERSEDED`/
 * `CANCELLED` ผ่านจุดนั้นไปแล้วและจะได้ `409` ทุกครั้ง
 *
 * สถานะที่หน้าจอยังไม่รู้จักก็ไม่มีปุ่มเช่นกัน (deny-safe: ปุ่มที่เดาว่าน่าจะกดได้
 * คือปุ่มที่จะถูกตอบ `409`)
 *
 * ⚠️ นี่คือ **UX layer** ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์ตรวจสถานะของทุกคำขอเสมอ
 * สิ่งที่ฟังก์ชันนี้ทำคือไม่เสนอปุ่มที่จะถูกปฏิเสธแน่ ๆ ให้ผู้ใช้กด
 */
export function canPublishForecastBatch(status: string): boolean {
  return status === VALIDATED_BATCH_STATUS;
}

/**
 * สิทธิ์ที่ปุ่ม Publish ต้องการ — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์
 *
 * **APPROVE ไม่ใช่ CREATE หรือ EDIT** เพราะ flow ข้อ 1.4 ระบุว่า "ผู้รับผิดชอบตรวจ
 * และ Publish" — การนำไฟล์เข้ากับการรับรองว่าแผนนี้ใช้ได้เป็นคนละการกระทำ และ
 * contract แยกสิทธิ์ของสองอย่างนี้ไว้ตั้งแต่ต้น
 */
export const FORECAST_PUBLISH_ACTION = 'APPROVE' as const;

/* ------------------------------------------------------- ค้นหาและตัวกรอง -- */

/**
 * กรองรายการด้วยคำค้นเดียว — เทียบ **ชื่อ revision และรหัสลูกค้า**
 *
 * สองคอลัมน์นี้คือสิ่งที่คนถืองานอยู่ในมือจะคัดลอกมาค้น: ชื่อ revision คือสิ่งที่
 * ลูกค้าเขียนไว้บนไฟล์ที่เพิ่งส่งมา และรหัสลูกค้าคือวิธีที่ผู้วางแผนไล่ดูว่ารายนี้
 * ส่งอะไรมาบ้างแล้ว
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่ — contract ของ
 * `GET /v1/forecast-imports` ไม่มีพารามิเตอร์ค้นหาอิสระ มีแต่ตัวกรองแบบตรงตัว
 * (`plant_code`/`customer_code`/`status`) ส่วนตัวกรองสถานะบนหน้าจอใช้ของจริงเหล่านั้น
 */
export function filterForecastImports(
  items: readonly ForecastImportBatchSummary[],
  query: string,
): readonly ForecastImportBatchSummary[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (batch) =>
      batch.version_label.toLocaleLowerCase().includes(needle) ||
      batch.customer_code.toLocaleLowerCase().includes(needle),
  );
}

/**
 * ค่าที่ตัวกรองสถานะส่งให้ `GET /v1/forecast-imports?status=` — คืน `undefined` เมื่อ
 * "ทุกสถานะ"
 *
 * ต่างจากช่องค้นหาซึ่งกรองฝั่งเบราว์เซอร์: `status` เป็นพารามิเตอร์ที่ contract รับ
 * จริง การกรองมันฝั่งเบราว์เซอร์จะกรองแค่ **หน้าที่โหลดมาแล้ว** แล้วผู้ใช้ที่เลือก
 * "ถูกแทนด้วยฉบับใหม่" จะเห็นรายการว่างทั้งที่มีใบเก่าอยู่ในหน้าถัดไป
 *
 * `undefined` ไม่ใช่สตริงว่าง — คีย์ที่ไม่ได้ระบุต้องไม่เดินทางไปเลย
 */
export function toForecastStatusFilter(status: string): string | undefined {
  const trimmed = status.trim();
  return trimmed === '' ? undefined : trimmed;
}

/* ------------------------------------------------------- ค่าที่ฟอร์มกรอก -- */

/**
 * จำนวนที่กรอกใช้ได้หรือยัง — ตรวจ **เป็นสตริง ไม่แปลงเป็น number แม้แต่ครั้งเดียว**
 *
 * `NUMERIC(18, 4)` เก็บได้ถึง 14 หลักหน้าจุดและ 4 หลังจุด ซึ่งกว้างกว่าที่ IEEE-754
 * double เก็บได้ครบ การตรวจด้วย `Number(value) >= 0` จะ "ผ่าน" ค่าที่ปัดไปแล้ว และ
 * ค่าที่ปัดแล้วคือจำนวนที่ลูกค้าไม่ได้พยากรณ์ไว้
 *
 * **ศูนย์ผ่าน** ต่างจาก `isPositiveQuantity()` ของใบสั่งขาย และความต่างนี้คือ D-093:
 * ขอบเขตของ forecast คือ `>= 0` เพราะ "วันนั้นเราคาดว่าไม่ต้องใช้เลย" เป็นคำพูดที่
 * มีความหมาย ต่างจากบรรทัดสั่งขายที่ศูนย์ชิ้นแปลว่าไม่ได้สั่งอะไร
 *
 * **ค่าติดลบถูกปฏิเสธที่นี่ด้วย** ทั้งที่เซิร์ฟเวอร์รับเข้าไปแล้วรายงานเป็น
 * `QUANTITY_MUST_NOT_BE_NEGATIVE` ราย line ได้ — เพราะบรรทัดที่รู้อยู่แล้วว่าจะผิด
 * ไม่ควรเดินทางออกไปเพื่อกลับมาเป็นแถวแดงในใบที่ publish ไม่ได้ ผู้ใช้แก้ที่ช่องตรง
 * หน้าได้ทันทีในรอบเดียว (ด่านจริงยังอยู่ที่เซิร์ฟเวอร์เสมอ สำหรับไฟล์ที่ไม่ได้ผ่าน
 * ฟอร์มนี้)
 */
export function isNonNegativeQuantity(value: string): boolean {
  return /^\d{1,14}(\.\d{1,4})?$/.test(value.trim());
}

/**
 * วันที่ที่กรอกเป็นวันตามปฏิทินที่มีอยู่จริงหรือไม่ — **ไม่ผ่าน `Date` เลย**
 *
 * `forecast_date` เป็น `DATE` ฝั่งเซิร์ฟเวอร์ (D-093) และ contract ปฏิเสธค่าที่มี
 * เวลาติดมาโดยตรง ค่าที่ออกจากฟอร์มนี้จึงต้องเป็นสตริง `YYYY-MM-DD` ที่ถูกส่งไป
 * **ตามตัวอักษร** ห้ามเดินผ่าน `new Date(...)` แล้วแปลงกลับ เพราะนั่นคือจุดที่วัน
 * เดียวกันจะกลายเป็นคนละวันตาม offset ของเครื่องผู้ใช้ — ซึ่งเป็นบั๊กเดียวกับที่
 * ชนิดของคอลัมน์ถูกเลือกมาเพื่อป้องกัน
 *
 * ตรวจ "มีอยู่จริง" ด้วยเลขวันในเดือน ไม่ใช่ด้วย `Date` ที่รับ `2026-02-30` แล้ว
 * เลื่อนให้เป็น `2026-03-02` เงียบ ๆ
 */
export function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (match === null) {
    return false;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1) {
    return false;
  }
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const lengths = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= lengths[month - 1];
}

/**
 * วันที่ของบรรทัดในรูปที่แสดงบนหน้าจอ — **ค่าเดิมทุกตัวอักษร**
 *
 * ไม่มีการแปลงใด ๆ เกิดขึ้นที่นี่โดยเจตนา และฟังก์ชันนี้มีอยู่เพื่อ **ประกาศ** สิ่ง
 * นั้น ไม่ใช่เพื่อทำงาน: ที่อื่นในแอปนี้ วันที่เป็น instant ที่ต้องถูกแปลงเป็นวัน
 * ตามเวลาเครื่อง (`requestedDeliveryDateLabel()` ของใบสั่งขาย) การมีฟังก์ชันชื่อ
 * เดียวกันที่นี่ซึ่ง **ไม่แปลงอะไรเลย** ทำให้ความต่างข้อนั้นอ่านออกจากโค้ด แทนที่จะ
 * เป็นสิ่งที่ใครสักคนจะ "แก้ให้เหมือนกัน" ในภายหลังแล้วทำให้วันเลื่อน
 */
export function forecastDateLabel(value: string): string {
  return value;
}

/** ค่าที่หัวใบของฟอร์มนำเข้า forecast ถืออยู่ — ดิบ ๆ ตามที่ผู้ใช้พิมพ์ */
export interface CreateForecastImportFields {
  readonly plantCode: string;
  readonly customerCode: string;
  readonly versionLabel: string;
  readonly sourceFileHash: string;
}

/**
 * หนึ่งบรรทัดที่ฟอร์มถืออยู่ — **ทั้งบรรทัดกรอกด้วยมือ**
 *
 * `lineNo` เป็น **สตริง** ที่นี่แม้ contract จะรับเป็นจำนวนเต็ม: ช่องกรอกถือสิ่งที่
 * ผู้ใช้พิมพ์ ซึ่งระหว่างพิมพ์มีสถานะ "ว่าง" และ "ยังพิมพ์ไม่เสร็จ" ที่ `number`
 * แทนไม่ได้ การแปลงเกิดครั้งเดียวตอนประกอบ body
 */
export interface ForecastLineDraft {
  readonly lineNo: string;
  readonly productCode: string;
  /** ค่าจาก `<input type="date">` — `YYYY-MM-DD` ที่ถูกส่งต่อตามตัวอักษร */
  readonly forecastDate: string;
  readonly quantity: string;
  readonly uomCode: string;
}

/** บรรทัดเปล่าที่ปุ่ม "เพิ่มบรรทัด" วางลงไป */
export const EMPTY_FORECAST_LINE_DRAFT: ForecastLineDraft = {
  lineNo: '',
  productCode: '',
  forecastDate: '',
  quantity: '',
  uomCode: '',
};

/** เลขบรรทัดที่ใช้ได้ — จำนวนเต็มบวกล้วน ไม่มีเครื่องหมายและไม่มีจุดทศนิยม */
function toLineNo(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d{1,9}$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  return parsed >= 1 ? parsed : null;
}

/**
 * ประกอบ body ของ `POST /v1/forecast-imports` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * เงื่อนไขที่ต้องครบ: สามช่องบังคับของหัวใบไม่ว่าง **มีอย่างน้อยหนึ่งบรรทัด** และ
 * ทุกบรรทัดกรอกครบและใช้ได้ — บรรทัดเดียวที่ผิดรูปทำให้ **ทั้งไฟล์** ประกอบไม่ได้
 * ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: forecast ที่ส่งไปโดยขาดบรรทัดที่ผู้ใช้ตั้งใจใส่ คือ demand
 * ที่หายไปจากแผนโดยไม่มีใครบอก ซึ่งเป็นความเสียหายชนิดเดียวกับที่ flow ข้อ 1.3
 * เขียนกฎขึ้นมาป้องกัน
 *
 * **ไม่ตรวจว่าคู่ (สินค้า, วันที่) ซ้ำกันเองหรือไม่** โดยตั้งใจ: นั่นเป็นกฎของ
 * เซิร์ฟเวอร์ (`DUPLICATE_FORECAST_LINE` ราย line) และหน้าจอนี้มีอยู่เพื่อ **แสดง**
 * ผลของกฎนั้น ด่านที่สองฝั่ง client จะทำให้กฎนั้นไม่มีวันถูกแสดงในฟอร์มนี้ และถ้า
 * นิยาม "ซ้ำ" ของสองฝั่งไม่ตรงกัน หน้าจอจะปฏิเสธไฟล์ที่เซิร์ฟเวอร์ยินดีจะรับ
 *
 * **ไม่ตรวจว่า `line_no` ซ้ำกันหรือไม่** ด้วยเหตุผลเดียวกัน — `400 DUPLICATE_LINE_NO`
 * เป็นคำตอบของเซิร์ฟเวอร์ และหน้าจอแปลมันให้อ่านออก
 *
 * ทุกรหัสถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอแต่ทำให้
 * รหัสสินค้า resolve ไม่ได้ แล้วบรรทัดดี ๆ จะกลายเป็น `PRODUCT_NOT_FOUND` ด้วยเหตุผล
 * ที่ผู้ใช้มองไม่เห็นเลยบนหน้าจอของตัวเอง
 *
 * `source_file_hash` ที่ว่างเปล่ากลายเป็น **คีย์ที่หายไปทั้งคีย์** ไม่ใช่สตริงว่างที่
 * ถูกส่งออกไป — `min_length=1` ฝั่งเซิร์ฟเวอร์จะตอบ `400` ให้กับสตริงว่าง ทั้งที่
 * ผู้ใช้แค่ไม่ได้กรอกช่องที่ไม่บังคับ
 */
export function toCreateForecastImportBody(
  fields: CreateForecastImportFields,
  lines: readonly ForecastLineDraft[],
): CreateForecastImportBody | null {
  const plantCode = fields.plantCode.trim();
  const customerCode = fields.customerCode.trim();
  const versionLabel = fields.versionLabel.trim();

  if (plantCode === '' || customerCode === '' || versionLabel === '' || lines.length === 0) {
    return null;
  }

  const serializedLines: CreateForecastImportLine[] = [];
  for (const line of lines) {
    const lineNo = toLineNo(line.lineNo);
    const productCode = line.productCode.trim();
    const forecastDate = line.forecastDate.trim();
    const quantity = line.quantity.trim();
    const uomCode = line.uomCode.trim();

    if (
      lineNo === null ||
      productCode === '' ||
      uomCode === '' ||
      !isCalendarDate(forecastDate) ||
      !isNonNegativeQuantity(quantity)
    ) {
      return null;
    }

    serializedLines.push({
      line_no: lineNo,
      product_code: productCode,
      forecast_date: forecastDate,
      quantity,
      uom_code: uomCode,
    });
  }

  const sourceFileHash = fields.sourceFileHash.trim();

  return {
    plant_code: plantCode,
    customer_code: customerCode,
    version_label: versionLabel,
    ...(sourceFileHash === '' ? {} : { source_file_hash: sourceFileHash }),
    lines: serializedLines,
  };
}
